import { useState } from 'react';
import {
  Table,
  Text,
  Group,
  Badge,
  ActionIcon,
  Alert,
  Card,
  Tooltip,
  Stack,
  ScrollArea,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconCheck,
  IconX,
  IconBrandGithub,
} from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { showSuccess } from '../../lib/notifications';
import { githubAccessApi } from '../../lib/api';
import { LoadingOverlay } from '../../components/LoadingOverlay';
import { PageTransition } from '../../components/PageTransition';

export function AdminGitHubAccessRequestsPage() {
  const queryClient = useQueryClient();
  const [error, setError] = useState('');

  const { data: githubAccessRequests, isLoading } = useQuery({
    queryKey: ['githubAccessRequests'],
    queryFn: async () => {
      try {
        const response = await githubAccessApi.listRequests();
        return response.data;
      } catch (error) {
        console.error('Failed to fetch GitHub access requests:', error);
        throw error;
      }
    },
  });

  const respondToGitHubRequest = useMutation({
    mutationFn: async ({ requestId, status }: { requestId: string; status: 'APPROVED' | 'REJECTED' }) => {
      const response = await githubAccessApi.respondToRequest(requestId, status);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['githubAccessRequests']);
      showSuccess('GitHub access request updated successfully');
    },
    onError: (error: any) => {
      setError(error.response?.data?.error || 'Failed to update GitHub access request');
    },
  });

  if (isLoading) {
    return <LoadingOverlay message="Loading..." />;
  }

  return (
    <PageTransition>
        <PageHeader
          title="GitHub Access Requests"
          description="Manage GitHub repository access requests from users"
        />

        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            mb="md"
            title="Error"
            onClose={() => setError('')}
            withCloseButton
          >
            {error}
          </Alert>
        )}

        <Card mt="md" shadow="sm" radius="md" withBorder>
          <ScrollArea>
          <Table>
            <thead>
              <tr>
                <th>User</th>
                <th>Repository</th>
                <th>Access Level</th>
                <th>Reason</th>
                <th>Requested At</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {githubAccessRequests?.map((request: any) => (
                <tr key={request.id}>
                  <td>
                    <Text>
                      {request.user.firstName} {request.user.lastName}
                    </Text>
                    <Text size="xs" color="dimmed">
                      {request.user.email}
                    </Text>
                  </td>
                  <td>
                    <Group spacing="xs">
                      <IconBrandGithub size={16} />
                      <Text>{request.repository}</Text>
                    </Group>
                  </td>
                  <td>
                    <Badge color={request.accessLevel === 'WRITE' ? 'red' : 'blue'}>
                      {request.accessLevel}
                    </Badge>
                  </td>
                  <td>
                    <Text size="sm">
                      {request.reason || 'No reason provided'}
                    </Text>
                  </td>
                  <td>
                    <Text size="sm" color="dimmed">
                      {new Date(request.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </Text>
                  </td>
                  <td>
                    <Badge
                      color={
                        request.status === 'PENDING'
                          ? 'yellow'
                          : request.status === 'APPROVED'
                          ? 'green'
                          : 'red'
                      }
                    >
                      {request.status}
                    </Badge>
                  </td>
                  <td>
                    {request.status === 'PENDING' && (
                      <Group spacing={4}>
                        <Tooltip label="Approve">
                          <ActionIcon
                            color="green"
                            onClick={() =>
                              respondToGitHubRequest.mutate({
                                requestId: request.id,
                                status: 'APPROVED',
                              })
                            }
                            loading={respondToGitHubRequest.isLoading}
                          >
                            <IconCheck size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Reject">
                          <ActionIcon
                            color="red"
                            onClick={() =>
                              respondToGitHubRequest.mutate({
                                requestId: request.id,
                                status: 'REJECTED',
                              })
                            }
                            loading={respondToGitHubRequest.isLoading}
                          >
                            <IconX size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          </ScrollArea>

          {(!githubAccessRequests || githubAccessRequests.length === 0) && (
            <Stack align="center" py="xl">
              <IconBrandGithub size={48} color="gray" />
              <Text color="dimmed" size="lg">
                No GitHub access requests found
              </Text>
              <Text color="dimmed" size="sm">
                Users will appear here when they request access to GitHub repositories
              </Text>
            </Stack>
          )}
        </Card>
    </PageTransition>
  );
}
